import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';
import AdminNavbar from '../components/AdminNavbar';
import { uploadImageToR2 } from '../utils/s3Upload';
import './AdminGallery.css';

export default function AdminGallery() {
    // Event states
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    
    // New Event Form states
    const [showEventForm, setShowEventForm] = useState(false);
    const [eventTitle, setEventTitle] = useState('');
    const [eventDescription, setEventDescription] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventCoverFile, setEventCoverFile] = useState(null);
    const eventCoverRef = useRef(null);
    
    // Images state for selected event
    const [eventImages, setEventImages] = useState([]);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const imageFilesRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const { data, error } = await supabase
                .from('gallery_events')
                .select(`
                    id, title, description, cover_image, event_date, created_at, is_pinned,
                    gallery_images (image)
                `)
                .order('is_pinned', { ascending: false })
                .order('event_date', { ascending: false });
            
            if (error) throw error;
            setEvents(data || []);
        } catch (error) {
            console.error("Error fetching events", error);
            setMessage({ type: 'danger', text: 'Failed to load events.' });
        }
    };

    const fetchEventImages = async (eventId) => {
        try {
            const { data, error } = await supabase
                .from('gallery_images')
                .select('*')
                .eq('event_id', eventId)
                .order('created_at', { ascending: false });
            
            if (error) throw error;
            setEventImages(data || []);
        } catch(error) {
            console.error("Error fetching images", error);
        }
    };

    const selectEvent = (event) => {
        setSelectedEvent(event);
        fetchEventImages(event.id);
        setShowEventForm(false);
        setMessage({ type: '', text: '' });
    };

    const handleCreateEvent = async (e) => {
        e.preventDefault();
        const MAX_SIZE = 20 * 1024 * 1024; // 20MB
        if (eventCoverFile && eventCoverFile.size > MAX_SIZE) {
            setMessage({ type: 'danger', text: 'Cover image must be under 20MB.' });
            return;
        }

        try {
            setLoading(true);
            setMessage({ type: '', text: 'Uploading cover image...' });
            
            let coverUrl = null;
            if (eventCoverFile) {
                coverUrl = await uploadImageToR2(eventCoverFile);
            }

            const { error } = await supabase
                .from('gallery_events')
                .insert([{
                    title: eventTitle,
                    description: eventDescription,
                    event_date: eventDate || null,
                    cover_image: coverUrl
                }]);
            
            if (error) throw error;
            
            setMessage({ type: 'success', text: 'Event created successfully!' });
            setShowEventForm(false);
            setEventTitle('');
            setEventDescription('');
            setEventDate('');
            setEventCoverFile(null);
            fetchEvents();
        } catch (error) {
            setMessage({ type: 'danger', text: `Failed to create event: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEvent = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure? This will delete the event and ALL its images.")) return;
        
        try {
            const { error } = await supabase
                .from('gallery_events')
                .delete()
                .eq('id', id);
            
            if (error) throw error;
            setMessage({ type: 'success', text: 'Event deleted!' });
            if (selectedEvent && selectedEvent.id === id) {
                setSelectedEvent(null);
            }
            fetchEvents();
        } catch(error) {
            setMessage({ type: 'danger', text: 'Failed to delete event.' });
        }
    };

    const handleTogglePin = async (id, currentPinnedStatus, e) => {
        e.stopPropagation();
        
        // If trying to pin, check if there are already 3 pinned
        if (!currentPinnedStatus) {
            const pinnedCount = events.filter(ev => ev.is_pinned).length;
            if (pinnedCount >= 3) {
                setMessage({ type: 'warning', text: 'You can only pin up to 3 events. Please unpin another event first.' });
                return;
            }
        }

        try {
            const { error } = await supabase
                .from('gallery_events')
                .update({ is_pinned: !currentPinnedStatus })
                .eq('id', id);
            
            if (error) throw error;
            fetchEvents();
        } catch (error) {
            console.error("Error toggling pin:", error);
            setMessage({ type: 'danger', text: 'Failed to update pin status.' });
        }
    };

    const handleUploadImages = async (e) => {
        e.preventDefault();
        const MAX_SIZE = 20 * 1024 * 1024; // 20MB
        
        const oversizedFiles = filesToUpload.filter(f => f.size > MAX_SIZE);
        if (oversizedFiles.length > 0) {
            setMessage({ type: 'danger', text: `Failed: Some files are over 20MB (${oversizedFiles.map(f => f.name).join(', ')})` });
            return;
        }

        if (filesToUpload.length === 0) {
            setMessage({ type: 'danger', text: 'Please select images.' });
            return;
        }

        try {
            setLoading(true);
            setMessage({ type: '', text: 'Uploading files...' });
            
            const uploadedUrls = [];
            let current = 0;
            const total = filesToUpload.length;

            for (const file of filesToUpload) {
                try {
                    const url = await uploadImageToR2(file);
                    uploadedUrls.push(url);
                    current++;
                    setUploadProgress(Math.round((current / total) * 100));
                } catch(err) {
                    console.error("Error uploading file:", file.name, err);
                }
            }

            setMessage({ type: '', text: 'Saving to database...' });

            const records = uploadedUrls.map(url => ({
                event_id: selectedEvent.id,
                image: url,
                title: '' // optional
            }));

            const { error } = await supabase
                .from('gallery_images')
                .insert(records);
            
            if (error) throw error;

            setMessage({ type: 'success', text: `Successfully uploaded ${uploadedUrls.length} images!` });
            setFilesToUpload([]);
            setUploadProgress(0);
            if(imageFilesRef.current) imageFilesRef.current.value = "";
            fetchEventImages(selectedEvent.id);
            fetchEvents(); // update count
        } catch (error) {
            setMessage({ type: 'danger', text: `Upload failed: ${error.message}` });
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteImage = async (id) => {
        if (!window.confirm("Delete this image?")) return;
        
        try {
            const { error } = await supabase
                .from('gallery_images')
                .delete()
                .eq('id', id);
            
            if (error) throw error;
            fetchEventImages(selectedEvent.id);
            fetchEvents(); // update count
        } catch(error) {
            setMessage({ type: 'danger', text: 'Failed to delete image.' });
        }
    };

    const isVideo = (url) => {
        if (!url) return false;
        const videoExts = ['.mp4', '.webm', '.ogg', '.mov', '.m4v'];
        return videoExts.some(ext => url.toLowerCase().endsWith(ext));
    };

    return (
        <div className="admin-gallery-container pt-5 pb-5">
            <div className="container-fluid px-2 px-md-5">
                <AdminNavbar />

                {message.text && (
                    <div className={`alert alert-${message.type} alert-dismissible fade show my-3`} role="alert">
                        {message.text}
                        <button type="button" className="close" onClick={() => setMessage({type: '', text: ''})} aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                )}

                <div className="row">
                    {/* Left Panel: Events List */}
                    <div className="col-lg-4 mb-4">
                        <div className="admin-card h-100">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4 className="mb-0">Gallery Folders</h4>
                                <button className="btn btn-primary btn-sm rounded-pill px-3" onClick={() => { setShowEventForm(!showEventForm); setSelectedEvent(null); }}>
                                    {showEventForm ? 'Cancel' : '+ New Folder'}
                                </button>
                            </div>

                            {showEventForm && (
                                <div className="event-form-card mb-4 p-3 bg-light rounded" style={{border: '1px solid #e2e8f0'}}>
                                    <form onSubmit={handleCreateEvent}>
                                        <div className="form-group">
                                            <label>Folder / Event Name *</label>
                                            <input type="text" className="form-control" value={eventTitle} onChange={e => setEventTitle(e.target.value)} required />
                                        </div>
                                        <div className="form-group mt-3">
                                            <label>Date</label>
                                            <input type="date" className="form-control" value={eventDate} onChange={e => setEventDate(e.target.value)} />
                                        </div>
                                        <div className="form-group mt-3">
                                            <label>Description</label>
                                            <textarea className="form-control" rows="2" value={eventDescription} onChange={e => setEventDescription(e.target.value)}></textarea>
                                        </div>
                                        <div className="form-group mt-3">
                                            <label>Cover Image (Required) *</label>
                                            <input type="file" className="form-control" accept="image/*" onChange={e => setEventCoverFile(e.target.files[0])} ref={eventCoverRef} required />
                                        </div>
                                        <button type="submit" className="upload-btn w-100 mt-4" disabled={loading}>
                                            {loading ? 'Creating...' : 'Create Folder'}
                                        </button>
                                    </form>
                                </div>
                            )}

                            <div className="events-list">
                                {events.map(ev => {
                                    const media = ev.gallery_images || [];
                                    const vCount = media.filter(m => isVideo(m.image)).length;
                                    const pCount = media.length - vCount;
                                    
                                    let breakdown = `${pCount} photo${pCount !== 1 ? 's' : ''}`;
                                    if (vCount > 0) breakdown += `, ${vCount} video${vCount !== 1 ? 's' : ''}`;

                                    const isSelected = selectedEvent?.id === ev.id;
                                    return (
                                        <div key={ev.id} className={`event-list-item ${isSelected ? 'active' : ''}`} onClick={() => selectEvent(ev)}>
                                            <img src={ev.cover_image || 'https://via.placeholder.com/150'} alt="Cover" className="event-list-img" />
                                            <div className="event-list-info">
                                                <h5>{ev.title} {ev.is_pinned && <i className="fa fa-thumbtack text-primary ms-1" title="Pinned"></i>}</h5>
                                                <p className="text-muted mb-0">{breakdown} | {ev.event_date ? new Date(ev.event_date).toLocaleDateString() : 'No date'}</p>
                                            </div>
                                            <div className="d-flex gap-2 ms-auto">
                                                <button className={`btn btn-sm ${ev.is_pinned ? 'btn-primary' : 'btn-outline-secondary'} pin-btn`} onClick={(e) => handleTogglePin(ev.id, ev.is_pinned, e)} title={ev.is_pinned ? 'Unpin' : 'Pin to Top'}>
                                                    <i className="fa fa-thumbtack"></i>
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger delete-event-btn" onClick={(e) => handleDeleteEvent(ev.id, e)} title="Delete Folder">
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    )
                                })}
                                {events.length === 0 && !showEventForm && (
                                    <p className="text-muted text-center mt-4 p-4 border rounded bg-light">No folders created yet.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: Selected Event Images */}
                    <div className="col-lg-8">
                        <div className="admin-card h-100 min-vh-50">
                            {selectedEvent ? (
                                <>
                                    <div className="d-flex justify-content-between align-items-start mb-4 border-bottom pb-3">
                                        <div>
                                            <h4 className="mb-1">{selectedEvent.title}</h4>
                                            <p className="text-muted mb-0">{selectedEvent.description || 'No description provided.'}</p>
                                        </div>
                                        <div className="text-end text-muted small bg-light px-3 py-1 rounded border">
                                            <i className="fa fa-calendar-alt me-2"></i>
                                            {selectedEvent.event_date ? new Date(selectedEvent.event_date).toDateString() : 'No date'}
                                        </div>
                                    </div>

                                    {/* Multi-upload section */}
                                    <form onSubmit={handleUploadImages} className="mb-4 bg-light p-4 rounded border">
                                        <label className="fw-bold text-secondary mb-3 d-block"><i className="fa fa-cloud-upload-alt me-2"></i>Upload Photos to this Folder</label>
                                        <div className="d-flex flex-column flex-md-row align-items-center gap-3">
                                            <input 
                                                type="file" 
                                                multiple 
                                                accept="image/*,video/*" 
                                                className="form-control file-input-multi"
                                                onChange={e => setFilesToUpload(Array.from(e.target.files))}
                                                ref={imageFilesRef}
                                            />
                                            <button type="submit" className="btn btn-success px-4 py-2 text-nowrap w-100" disabled={loading || filesToUpload.length === 0} style={{maxWidth: '200px'}}>
                                                {loading ? 'Uploading...' : `Upload ${filesToUpload.length > 0 ? filesToUpload.length : ''} Files`}
                                            </button>
                                        </div>
                                        {uploadProgress > 0 && uploadProgress < 100 && (
                                            <div className="progress mt-3" style={{height: '10px'}}>
                                                <div className="progress-bar progress-bar-striped progress-bar-animated bg-success" role="progressbar" style={{width: `${uploadProgress}%`}}></div>
                                            </div>
                                        )}
                                    </form>

                                    <div className="row admin-grid">
                                        {eventImages.map(img => (
                                            <div className="col-md-6 col-lg-4 mb-4" key={img.id}>
                                                <div className="admin-image-item">
                                                    {isVideo(img.image) ? (
                                                        <video src={`${img.image}#t=0.5`} preload="metadata" muted loop onMouseOver={e => e.target.play()} onMouseOut={e => e.target.pause()} />
                                                    ) : (
                                                        <img src={img.image} alt="Gallery" />
                                                    )}
                                                    <div className="item-overlay d-flex flex-column justify-content-end p-2">
                                                        <button onClick={() => handleDeleteImage(img.id)} className="btn btn-sm btn-danger w-100">
                                                            <i className="fa fa-trash me-2"></i> Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {eventImages.length === 0 && (
                                            <div className="col-12 text-center py-5 border border-dashed rounded mt-3">
                                                <i className="fa fa-images fa-3x text-muted mb-3 opacity-50"></i>
                                                <p className="text-muted mb-0">No images in this folder yet.</p>
                                                <p className="text-muted small">Upload some using the form above.</p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted" style={{minHeight: '400px'}}>
                                    <i className="fa fa-folder-open fa-4x mb-3 opacity-25"></i>
                                    <h5>Select a folder</h5>
                                    <p>Select a folder from the left panel to view and manage its photos.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
